import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "framer-motion";

const NOTES = [
  {
    id: "pav",
    side: "left",
    title: "MASALA PAV SUB",
    body: "Soft lamba pav loaf baked for us daily, butter-toasted on the tawa per order.",
    story: "The pav rode into Bombay with the Portuguese — pão turned pav. Irani and Goan bakeries still fire it before dawn and truck it to tapris warm. Ours is baked lamba, sliced, then butter-toasted on the tawa — never before you order.",
  },
  {
    id: "thecha",
    side: "right",
    title: "THECHA KICK",
    body: 'Fried green chilli on top; Kolhapuri thecha on request. Ask for "full risk".',
    story: "Thecha is Kolhapur's gift and its warning — green chillies and garlic pounded on stone, the farmer's chutney. We fry the chilli whole, the way the trek stalls on Sinhagad do it. Not a garnish; a dare.",
  },
  {
    id: "vada",
    side: "right",
    title: "THE LONG VADA",
    body: "One continuous besan-crusted batata vada — our signature. Fried to order in 90 seconds, potato in every bite.",
    story: "1966, outside Dadar station: the first batata vada hits the fryer for mill workers sprinting for the local. Sixty years of rush hour later, we took that same vada and uncurled it — potato, end to end.",
  },
  {
    id: "chutney",
    side: "left",
    title: "CHUTNEY TRIO",
    body: "Teekha green, meethi imli and dry lasun — layered so every bite lands different.",
    story: "Every tapri guards its chutney ratio like a family secret. Dry lasun from the Deccan, imli for the meethi balance, green for courage. Three chutneys, one queue-worthiness test.",
  },
  {
    id: "crisp",
    side: "bottom",
    title: "NO SOGGY BUSINESS",
    body: "Dry chutney base keeps the pav crisp from first bite to last.",
    story: "Ask any Dadar regular: the vada pav that survives a Virar fast local is the one with dry chutney at the base. We applied the same physics to a footlong. Science, but tastier.",
  },
];

const NoteCard = ({ note, isActive, isOpen, onEnter, onLeave, onToggle }) => (
  <button
    type="button"
    data-testid={`anatomy-note-${note.id}`}
    onMouseEnter={onEnter}
    onMouseLeave={onLeave}
    onFocus={onEnter}
    onClick={() => {
      onEnter();
      onToggle();
    }}
    className={`block w-full rounded-2xl border-4 p-5 text-left transition-[border-color,box-shadow,transform] duration-200 ${
      isActive
        ? "-translate-y-1 border-solid border-mirchi bg-pav sticker-shadow"
        : "border-dashed border-mirchi/50 bg-pav hover:border-mirchi"
    }`}
  >
    <div className="flex items-center justify-between gap-2">
      <span className={`inline-block h-3 w-3 rounded-full border-2 border-kadak ${isActive ? "bg-mirchi" : "bg-pav"}`} />
      <span data-testid={`anatomy-story-toggle-${note.id}`} className="font-bungee text-[10px] tracking-widest text-mirchi/80">
        {isOpen ? "CLOSE −" : "ORIGIN STORY +"}
      </span>
    </div>
    <h3 className="mt-2 font-anton text-base uppercase tracking-wide text-kadak">{note.title}</h3>
    <p className="mt-2 text-sm leading-relaxed text-kadak/70">{note.body}</p>
    <motion.span
      initial={false}
      animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="block overflow-hidden"
    >
      <span className="mt-3 block border-t-2 border-dashed border-kadak/20 pt-3 font-shrikhand text-base leading-snug text-chutney">
        {note.story}
      </span>
    </motion.span>
  </button>
);

const CHIPS = {
  pav: [
    { top: "30%", label: "PAV" },
    { top: "74%", label: "PAV" },
  ],
  thecha: [
    { top: "13%", label: "CHILLI" },
    { top: "39%", label: "THECHA" },
  ],
  vada: [{ top: "49%", label: "LONG VADA" }],
  chutney: [{ top: "62%", label: "CHUTNEY ×3" }],
  crisp: [{ top: "74%", label: "CRISP BASE" }],
};

const PhotoStage = ({ active }) => {
  const wrapRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const px = useSpring(mx, { stiffness: 60, damping: 16 });
  const py = useSpring(my, { stiffness: 60, damping: 16 });
  const imgX = useTransform(px, [-0.5, 0.5], [-12, 12]);
  const imgY = useTransform(py, [-0.5, 0.5], [-10, 10]);
  const tilt = useTransform(px, [-0.5, 0.5], [-2.5, 2.5]);

  const onMove = (e) => {
    const r = wrapRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div ref={wrapRef} onMouseMove={onMove} onMouseLeave={reset} className="relative mx-auto max-w-sm" data-testid="anatomy-sub-stage">
      <div className="pointer-events-none absolute -top-11 left-0 right-0 z-10 flex justify-center gap-8">
        {[0, 1, 2].map((i) => (
          <motion.svg
            key={i}
            viewBox="0 0 24 48"
            className="h-10 w-5"
            fill="none"
            stroke="#221A10"
            strokeWidth="5"
            strokeLinecap="round"
            animate={{ y: [0, -8, 0], opacity: [0.2, 0.7, 0.2] }}
            transition={{ repeat: Infinity, duration: 2.2, delay: i * 0.35, ease: "easeInOut" }}
          >
            <path d="M12 40 q8 -12 0 -24 q-6 -10 2 -14" />
          </motion.svg>
        ))}
      </div>

      <motion.div
        animate={{ y: [0, -9, 0], rotate: [0, 0.8, 0] }}
        transition={{ repeat: Infinity, duration: 5.5, ease: "easeInOut" }}
        className="overflow-hidden rounded-3xl border-4 border-kadak sticker-shadow"
      >
        <motion.img
          src="/assets/anatomy-photo.jpg"
          alt="Exploded Vada Sub — layers floating apart"
          data-testid="anatomy-photo"
          style={{ x: imgX, y: imgY, rotate: tilt }}
          animate={{ scale: [1.08, 1.13, 1.08] }}
          transition={{ repeat: Infinity, duration: 8, ease: "easeInOut" }}
          className="w-full"
        />
      </motion.div>

      <AnimatePresence mode="popLayout">
        {active &&
          CHIPS[active].map((c, i) => (
            <motion.span
              key={`${active}-${i}`}
              initial={{ opacity: 0, scale: 0.4, x: 12 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.4 }}
              transition={{ type: "spring", stiffness: 300, damping: 18 }}
              className="absolute -right-2 z-20 -translate-y-1/2 rounded-full border-2 border-kadak bg-mirchi px-3 py-1 font-bungee text-[10px] tracking-widest text-pav sticker-shadow-sm"
              style={{ top: c.top }}
            >
              {c.label}
            </motion.span>
          ))}
      </AnimatePresence>

      <motion.span
        animate={{ y: [0, -8, 0], rotate: [-6, -2, -6] }}
        transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
        className="absolute -left-6 top-1/3 rounded-full border-4 border-kadak bg-taxi px-3 py-1 font-bungee text-[10px] tracking-widest text-kadak sticker-shadow-sm"
      >
        GARMA GARAM
      </motion.span>
    </div>
  );
};

export const Anatomy = () => {
  const [hover, setHover] = useState(null);
  const [auto, setAuto] = useState(0);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (hover) return undefined;
    const t = setInterval(() => setAuto((i) => (i + 1) % NOTES.length), 2400);
    return () => clearInterval(t);
  }, [hover]);

  const active = hover ?? NOTES[auto].id;
  const left = NOTES.filter((n) => n.side === "left");
  const right = NOTES.filter((n) => n.side === "right");
  const bottom = NOTES.find((n) => n.side === "bottom");
  const toggle = (id) => setOpenId((o) => (o === id ? null : id));
  const cardProps = (n) => ({
    note: n,
    isActive: active === n.id,
    isOpen: openId === n.id,
    onEnter: () => setHover(n.id),
    onLeave: () => setHover(null),
    onToggle: () => toggle(n.id),
  });

  return (
    <section id="anatomy" data-testid="anatomy-section" className="scroll-mt-24 overflow-hidden bg-pav py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <p className="text-center font-bungee text-xs tracking-[0.35em] text-mirchi sm:text-sm">THE PRODUCT</p>
        <h2 className="mt-4 text-center font-shrikhand text-4xl text-kadak sm:text-5xl lg:text-6xl">
          Anatomy of the <span className="text-chutney">Vada Sub</span>
        </h2>
        <p className="mt-4 text-center font-anton text-sm uppercase tracking-wide text-kadak/60 sm:text-base">
          Five moves. One icon. It never sits still. Tap a card — it tells a story.
        </p>

        <div className="mt-12 grid grid-cols-1 items-center gap-8 lg:grid-cols-[1fr_1.35fr_1fr]">
          <div className="order-2 space-y-6 lg:order-1">
            {left.map((n) => (
              <NoteCard key={n.id} {...cardProps(n)} />
            ))}
          </div>

          <motion.div
            className="order-1 lg:order-2"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <PhotoStage active={active} />
            <p className="mt-3 text-center font-bungee text-[10px] tracking-[0.3em] text-kadak/40">
              GARMA GARAM • FRIED TO ORDER • REAL SHOOT WHEN KHOKA #1 FIRES UP
            </p>
          </motion.div>

          <div className="order-3 space-y-6">
            {right.map((n) => (
              <NoteCard key={n.id} {...cardProps(n)} />
            ))}
          </div>
        </div>

        <div className="mx-auto mt-8 max-w-md">
          <NoteCard {...cardProps(bottom)} />
        </div>
      </div>
    </section>
  );
};
